<template>
  <div>
    <b-card>
      <b-card-header>
        <h5>{{ selectedReportKey ? currentReportConfig.label : 'Logistics Reports' }}</h5>
      </b-card-header>
      <b-card-body>
        <validation-observer ref="observer">
          <b-form @submit.prevent="generateReport">
            <b-row>
              <b-col cols="12">
                <b-form-group label="Report">
                  <validation-provider
                    name="Report"
                    rules="required"
                  >
                    <b-form-select
                      id="report_key"
                      v-model="selectedReportKey"
                      :options="reportOptions"
                      value-field="key"
                      text-field="label"
                      class="text-capitalize"
                    >
                      <b-form-select-option :value="null">
                        Select report...
                      </b-form-select-option>
                    </b-form-select>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
            <template v-if="selectedReportKey">
              <template v-if="isWarehouseStockMovementReport">
                <b-row>
                  <b-col cols="6">
                    <b-form-group label="Warehouse">
                      <validation-provider
                        name="Warehouse"
                        rules="required"
                      >
                        <b-form-select
                          id="warehouse_id"
                          v-model="filter.warehouse_id"
                          :options="warehouseOptions"
                          value-field="id"
                          text-field="name"
                          class="text-capitalize"
                        >
                          <b-form-select-option :value="null">
                            Select warehouse...
                          </b-form-select-option>
                        </b-form-select>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </template>
              <template v-else-if="isTfDetailReport">
                <b-row>
                  <b-col cols="6">
                    <b-form-group label="Location">
                      <validation-provider
                        v-if="isSuperUser"
                        name="Location"
                        rules="required"
                      >
                        <b-form-select
                          id="location_id"
                          v-model="filter.region_id"
                          :options="regionOptions"
                          value-field="id"
                          text-field="name"
                          class="text-capitalize"
                        >
                          <b-form-select-option :value="null">
                            Select location...
                          </b-form-select-option>
                        </b-form-select>
                      </validation-provider>
                      <div
                        v-else
                        class="text-muted small mb-0"
                      >
                        Location is set to your assigned station.
                      </div>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group label="TF Number">
                      <validation-provider
                        name="TF Number"
                        rules="required"
                      >
                        <b-form-input
                          id="tf_number"
                          v-model="filter.tf_number"
                          placeholder="Enter TF Number"
                        />
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </template>
              <template v-else>
                <b-row>
                  <b-col cols="6">
                    <b-form-group label="Region">
                      <b-form-checkbox
                        v-model="selectAllRegions"
                        class="mb-1"
                      >
                        Select All Regions
                      </b-form-checkbox>
                      <validation-provider
                        v-if="!selectAllRegions"
                        name="Region"
                        rules="required"
                      >
                        <b-form-select
                          id="region_id"
                          v-model="filter.region_id"
                          :options="regionOptions"
                          value-field="id"
                          text-field="name"
                          class="text-capitalize"
                          @change="changeRegion(filter.region_id)"
                        >
                          <b-form-select-option :value="null">
                            Select region...
                          </b-form-select-option>
                        </b-form-select>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col cols="6">
                    <b-form-group>
                      <b-form-checkbox
                        v-model="selectAllWarehouses"
                        class="mb-1"
                        @change="onSelectAllWarehousesChange"
                      >
                        Select All Warehouses
                      </b-form-checkbox>
                      <validation-provider
                        v-if="!selectAllWarehouses"
                        name="Warehouse"
                        rules="required"
                      >
                        <div class="warehouse-selection">
                          <template v-if="filter.region_id">
                            <div
                              v-for="warehouse in warehouseOptions"
                              :key="warehouse.id"
                              class="warehouse-option"
                            >
                              <b-form-checkbox
                                inline
                                :checked="filter.warehouse_id && filter.warehouse_id.includes(warehouse.id)"
                                class="warehouse-checkbox"
                                @change="toggleWarehouse(warehouse.id)"
                              >
                                {{ warehouse.name }}
                              </b-form-checkbox>
                            </div>
                          </template>
                          <template v-else>
                            <div class="text-muted small mb-0">
                              Please select a region first to load warehouses.
                            </div>
                          </template>
                        </div>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col cols="3">
                    <b-form-group label="Start date">
                      <validation-provider
                        name="Start date"
                        rules="required"
                      >
                        <b-form-datepicker
                          id="start_date"
                          v-model="filter.start_date"
                          class="w-100"
                          :max="filter.end_date || null"
                          placeholder="Select Start Date"
                        />
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col cols="3">
                    <b-form-group label="End date">
                      <validation-provider
                        name="End date"
                        rules="required"
                      >
                        <b-form-datepicker
                          id="end_date"
                          v-model="filter.end_date"
                          class="w-100"
                          :min="filter.start_date || null"
                          placeholder="Select End Date"
                        />
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </template>

            </template>

            <b-row
              v-if="isSuperUser && selectedReportKey && !isTfDetailReport && !isWarehouseStockMovementReport"
              class="mt-2"
            >
              <b-col cols="12">
                <b-form-checkbox v-model="showAllWarehouses">
                  Show all warehouses
                </b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col
                cols="12"
                class="text-right"
              >
                <b-button
                  type="submit"
                  variant="success"
                  :disabled="isGenerateDisabled"
                >
                  <b-spinner
                    v-if="loading"
                    small
                    label="Loading"
                  />
                  <span>{{ loading ? 'Generating...' : 'Generate report' }}</span>
                </b-button>
                <b-button
                  variant="info"
                  class="ml-2"
                  :disabled="loading || !tableItems.length"
                  @click="exportPdf"
                >
                  Export PDF
                </b-button>
                <b-button
                  variant="primary"
                  class="ml-2"
                  :disabled="loading || !tableItems.length"
                  @click="exportExcel"
                >
                  Export Excel
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>

        <b-alert
          v-if="errorMessage"
          variant="danger"
          show
          class="mt-3"
        >
          {{ errorMessage }}
        </b-alert>

        <template v-if="selectedReportKey">
          <div
            v-if="isTfDetailReport && tfDetailSummary"
            class="mt-3"
          >
            <b-row>
              <b-col
                cols="6"
                md="4"
              >
                <div class="text-muted small">
                  Transporter (First Name)
                </div>
                <div>{{ tfDetailSummary.transporterFirstName }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
              >
                <div class="text-muted small">
                  Driver
                </div>
                <div>{{ tfDetailSummary.driverName }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
              >
                <div class="text-muted small">
                  Plate Number
                </div>
                <div>{{ tfDetailSummary.plateNumber }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
              >
                <div class="text-muted small">
                  Source
                </div>
                <div>{{ tfDetailSummary.sourceName }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
                class="mt-2"
              >
                <div class="text-muted small">
                  Destination
                </div>
                <div>{{ tfDetailSummary.destinationName }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
                class="mt-2"
              >
                <div class="text-muted small">
                  Loss % (Dispatch vs Purchase)
                </div>
                <div>{{ tfDetailSummary.lossDispatchVsPurchase }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
                class="mt-2"
              >
                <div class="text-muted small">
                  Bales Loaded (&gt; 0)
                </div>
                <div>{{ tfDetailSummary.loadedBalesCount }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
                class="mt-2"
              >
                <div class="text-muted small">
                  Loading Time
                </div>
                <div>{{ tfDetailSummary.loadingTimeMinutes }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
                class="mt-2"
              >
                <div class="text-muted small">
                  Bales Received (&gt; 0)
                </div>
                <div>{{ tfDetailSummary.receivedBalesCount }}</div>
              </b-col>
              <b-col
                cols="6"
                md="4"
                class="mt-2"
              >
                <div class="text-muted small">
                  Loss % (Dispatch vs Receive)
                </div>
                <div>{{ tfDetailSummary.lossDispatchVsReceive }}</div>
              </b-col>
            </b-row>
          </div>
          <div
            v-if="isTfDetailReport && records.length"
            class="mt-3"
          >
            <b-form-group
              label="TF Detail View"
              class="mb-0"
            >
              <b-form-checkbox
                v-model="showUnreceivedBalesOnly"
                switch
              >
                Show only unreceived bales
              </b-form-checkbox>
              <small class="text-muted">
                {{ tfDetailToggleHelpText }}
              </small>
            </b-form-group>
          </div>
          <b-table
            striped
            hover
            small
            responsive
            class="mt-3"
            :items="tableItems"
            :fields="currentColumns"
            :empty-text="currentEmptyText"
            :tbody-tr-class="tbodyTrClass"
            :sort-compare="sortCompare"
          />

          <div
            v-if="tableRecordCountText"
            class="mt-2 text-muted"
          >
            {{ tableRecordCountText }}
          </div>
        </template>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BForm,
    BFormGroup,
    BButton,
    BFormSelect,
    BFormSelectOption,
    BFormDatepicker,
    BFormInput,
    BAlert,
    BSpinner,
    BFormCheckbox,
} from 'bootstrap-vue'
import store from '@/store'
import stockProductionStoreModule from '@/views/cromis/reports/stock_production/stockProductionStoreModule'
import useStockProductionList from '@/views/cromis/reports/stock_production/useStockProductionList'
import { jsPDF as JsPDF } from 'jspdf'
import 'jspdf-autotable'
import { $themeConfig } from '@themeConfig'
import { addReportHeader, createReportFooterCallback } from '@core/utils/pdfReportHelpers'
import exportTableToExcel from '@core/utils/excelReportHelpers'
import logo from '@/assets/images/logo/logo3.png'
import {
    buildTfDetailTotalsRow,
    countTfDetailLoadedBales,
    countTfDetailReceivedBales,
    isTfDetailRecordLoaded,
    isTfDetailRecordUnreceived,
} from '@/views/cromis/reports/stock_production/stockProductionReportHelpers'
import {
    ref, computed, onMounted, onUnmounted, watch,
} from '@vue/composition-api'

const TF_DETAIL_SUMMARY_KEY = '__summary'

export default {
    components: {
        BRow,
        BCol,
        BCard,
        BCardHeader,
        BCardBody,
        BTable,
        BForm,
        BFormGroup,
        BButton,
        BFormSelect,
        BFormSelectOption,
        BFormDatepicker,
        BFormInput,
        BAlert,
        BSpinner,
        BFormCheckbox,
        ValidationObserver,
        ValidationProvider,
    },
    setup() {
        console.log('StockProduction setup starting...')
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        console.log('userData loaded:', userData)
        const role = ref((userData.roles && userData.roles[0]) || {})
        const isSuperUser = ref(
            userData.super_user === true
      || (userData.roles && userData.roles[0] && (
          userData.roles[0].id === 1
        || (typeof userData.roles[0].title === 'string' && userData.roles[0].title.toLowerCase().includes('super'))
      )),
        )
        console.log('isSuperUser:', isSuperUser.value)
        const showAllWarehouses = ref(!!isSuperUser.value)
        const locId = ref(userData.station ? userData.station.location : null)
        const regions = ref([])
        const warehouses = ref([])
        const filter = ref({
            region_id: null,
            warehouse_id: null,
            start_date: null,
            end_date: null,
            tf_number: '',
        })
        const companyName = $themeConfig.app.appName
        const { companyAddress } = $themeConfig.app
        const errorMessage = ref(null)
        const CROMIS_STORE_MODULE_NAME = 'cromis-stock-production'
        const hasGeneratedReport = ref(false)
        const showUnreceivedBalesOnly = ref(false)

        // Missing reactive variables
        const selectedReportKey = ref(null)
        const reportOptions = ref([
            { key: 'warehouse_stock_movements', label: 'Warehouse Stock Movements' },
            { key: 'tf_summary', label: 'TF Summary Report' },
            { key: 'tf_detail_by_tf_number', label: 'TF Detail By TF Number' },
        ])

        if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) {
            store.registerModule(CROMIS_STORE_MODULE_NAME, stockProductionStoreModule)
        }

        onUnmounted(() => {
            if (store.hasModule(CROMIS_STORE_MODULE_NAME)) {
                store.unregisterModule(CROMIS_STORE_MODULE_NAME)
            }
        })

        const {
            records,
            loading,
            totalRecords,
            fetchReport,
        } = useStockProductionList()

        const selectAllRegions = ref(false)
        const selectAllWarehouses = ref(false)
        const regionOptions = ref([])
        const warehouseOptions = ref([])

        const formatDate = value => {
            if (!value) return null
            if (typeof value === 'string') return value
            if (value instanceof Date) return value.toISOString().split('T')[0]
            return String(value).split('T')[0]
        }

        const formatNumber = value => {
            if (value === null || value === undefined || value === '') return ''
            const normalized = String(value).replace(/,/g, '')
            const number = Number(normalized)
            if (Number.isNaN(number)) return value
            let formatted = number.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
            })
            formatted = formatted.replace(/(\.[0-9]*[1-9])0+$/, '$1').replace(/\.0+$/, '')
            return formatted
        }

        const parseNumber = value => {
            if (value === null || value === undefined || value === '') return null
            const normalized = String(value).replace(/,/g, '')
            const number = Number(normalized)
            return Number.isNaN(number) ? null : number
        }

        const calcLossPercentDispatchVsPurchase = (totalDispatch, totalPurchase) => {
            if (totalPurchase === null || totalDispatch === null || totalPurchase === 0) return null
            return ((totalDispatch - totalPurchase) / totalPurchase) * 100
        }

        const calcLossPercentDispatchVsReceive = (totalReceive, totalDispatch) => {
            if (totalDispatch === null || totalReceive === null || totalDispatch === 0) return null
            return ((totalReceive - totalDispatch) / totalDispatch) * 100
        }

        const formatPercent = value => {
            if (value === null || value === undefined || Number.isNaN(value)) return 'N/A'
            return `${value.toFixed(2)}%`
        }

        const formatMinutes = value => {
            if (value === null || value === undefined || Number.isNaN(value)) return 'N/A'
            return `${value.toFixed(2)} min`
        }

        const getFirstNonEmpty = (item, keys) => {
            if (!item) return null
            for (let i = 0; i < keys.length; i += 1) {
                const key = keys[i]
                const raw = item[key]
                if (raw !== null && raw !== undefined && String(raw).trim() !== '') {
                    return raw
                }
            }
            return null
        }

        const parseDateTime = value => {
            if (!value) return null
            if (value instanceof Date) return value
            const normalized = String(value).trim()
            if (!normalized) return null
            const safe = normalized.includes('T') ? normalized : normalized.replace(' ', 'T')
            const date = new Date(safe)
            return Number.isNaN(date.getTime()) ? null : date
        }

        const getFirstNonEmptyFromRecords = (items, keys) => {
            if (!items || items.length === 0) return null
            for (let i = 0; i < items.length; i += 1) {
                const value = getFirstNonEmpty(items[i], keys)
                if (value !== null && value !== undefined && String(value).trim() !== '') {
                    return value
                }
            }
            return null
        }

        const getNumericFromItem = (item, keys) => {
            const raw = getFirstNonEmpty(item, keys)
            return parseNumber(raw)
        }

        const sumNumeric = (items, keys) => {
            if (!items || items.length === 0) return null
            let total = 0
            let hasValue = false
            items.forEach(item => {
                const value = getNumericFromItem(item, keys)
                if (value !== null) {
                    total += value
                    hasValue = true
                }
            })
            return hasValue ? total : null
        }

        const reportConfigMap = {
            warehouse_stock_movements: {
                key: 'warehouse_stock_movements',
                label: 'Warehouse Stock Movements',
                endpoint: 'reports/warehouse_stock_movements',
                columns: [
                    { key: 'warehouse_name', label: 'Warehouse', sortable: true },
                    { key: 'tfNumber', label: 'TF Number', sortable: true },
                    { key: 'Source_Destination', label: 'Source / Destination', sortable: true },
                    { key: 'transaction_date', label: 'Transaction Date', sortable: true },
                    {
                        key: 'Bales_Received', label: 'Bales Received', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Mass_Received', label: 'Mass Received', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Bales_Dispatched', label: 'Bales Dispatched', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Mass_Dispatched', label: 'Mass Dispatched', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Weight_loss', label: 'Weight Loss', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
            },
            tf_summary: {
                key: 'tf_summary',
                label: 'TF Summary Report',
                endpoint: 'reports/tf_summary',
                columns: [
                    { key: 'region_name', label: 'Warehouse', sortable: true },
                    { key: 'Transport_Category', label: 'Transport Category', sortable: true },
                    { key: 'transporter_name', label: 'Transporter Name', sortable: true },
                    { key: 'Date Created', label: 'Date Created', sortable: true },
                    { key: 'loading_start', label: 'Load Started', sortable: true },
                    { key: 'loading_end', label: 'Load Ended', sortable: true },
                    { key: 'offloading_start', label: 'Offl Started', sortable: true },
                    { key: 'offloading_end', label: 'Offl Ended', sortable: true },
                    { key: 'system_tf_number', label: 'TF Number', sortable: true },
                    { key: 'source_name', label: 'Source Name', sortable: true },
                    { key: 'destination_name', label: 'Destination Name', sortable: true },
                    { key: 'driver_licence', label: 'Driver ID', sortable: true },
                    { key: 'driver_name', label: 'Driver Name', sortable: true },
                    { key: 'driver_phone', label: 'Driver Phone#', sortable: true },
                    {
                        key: 'Bales_Loaded', label: 'Bale Dispatched', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Bales_Received', label: 'Bale Received', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'mass_loaded', label: 'Mass Dispatched', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'mass_received', label: 'Mass Received', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'weight_loss', label: 'Weight Variance', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
            },
            tf_detail_by_tf_number: {
                key: 'tf_detail_by_tf_number',
                label: 'TF Detail By TF Number',
                endpoint: 'reports/tf_detail_by_tf_number',
                columns: [
                    { key: 'scanner_full_Name', label: 'Scanner Name', sortable: true },
                    { key: 'loaded_at', label: 'Loaded at', sortable: true },
                    { key: 'MarkedAs', label: 'Status', sortable: true },
                    { key: 'loaded_bale', label: 'Bales Loaded', sortable: true },
                    {
                        key: 'purchase_mass', label: 'Purchase Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    {
                        key: 'Dispatched', label: 'Dispatch Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                    { key: 'received_bale', label: 'Bales Received', sortable: true },
                    { key: 'received_by', label: 'Received By', sortable: true },
                    { key: 'received_at', label: 'Received at', sortable: true },
                    {
                        key: 'Received', label: 'Received Mass', sortable: true, formatter: value => formatNumber(value),
                    },
                ],
            },
        }

        const currentReportConfig = computed(() => {
            const key = selectedReportKey.value
            console.log('currentReportConfig computed, key:', key)
            const config = key && reportConfigMap[key] ? reportConfigMap[key] : reportConfigMap.warehouse_stock_movements
            console.log('currentReportConfig result:', config)
            return config
        })
        const currentColumns = computed(() => {
            const config = currentReportConfig.value
            console.log('currentColumns computed, config:', config)
            return config ? config.columns : []
        })
        const isTfDetailReport = computed(() => selectedReportKey.value === 'tf_detail_by_tf_number')
        const isWarehouseStockMovementReport = computed(() => selectedReportKey.value === 'warehouse_stock_movements')
        const loadedTfDetailRecords = computed(() => {
            if (!isTfDetailReport.value) return []
            return records.value.filter(item => isTfDetailRecordLoaded(item))
        })
        const tfDetailLoadedBalesCount = computed(() => countTfDetailLoadedBales(records.value))
        const tfDetailReceivedBalesCount = computed(() => countTfDetailReceivedBales(records.value))
        const tfDetailUnreceivedBalesCount = computed(() => loadedTfDetailRecords.value.filter(item => isTfDetailRecordUnreceived(item)).length)
        const displayedTfDetailRecords = computed(() => {
            if (!isTfDetailReport.value) return []
            if (showUnreceivedBalesOnly.value) {
                return loadedTfDetailRecords.value.filter(item => isTfDetailRecordUnreceived(item))
            }
            return loadedTfDetailRecords.value
        })
        const displayedTfDetailTotalsRow = computed(() => {
            if (!isTfDetailReport.value) return null
            return buildTfDetailTotalsRow(loadedTfDetailRecords.value)
        })
        const hasWarehouseId = computed(() => {
            if (Array.isArray(filter.value.warehouse_id)) {
                return filter.value.warehouse_id.length > 0
            }
            return filter.value.warehouse_id !== null
                && filter.value.warehouse_id !== undefined
                && String(filter.value.warehouse_id).trim() !== ''
        })
        const tableItems = computed(() => {
            if (isTfDetailReport.value) {
                if (displayedTfDetailTotalsRow.value) {
                    return [{ ...displayedTfDetailTotalsRow.value }, ...displayedTfDetailRecords.value]
                }
                return displayedTfDetailRecords.value
            }
            return records.value
        })
        const currentEmptyText = computed(() => {
            if (isTfDetailReport.value && hasGeneratedReport.value) {
                return showUnreceivedBalesOnly.value
                    ? 'No unreceived loaded bales found for the selected TF report'
                    : 'No loaded bales found for the selected TF report'
            }
            return `No ${currentReportConfig.value.label.toLowerCase()} found`
        })
        const tfDetailToggleHelpText = computed(() => (
            showUnreceivedBalesOnly.value
                ? `Listing ${formatNumber(tfDetailUnreceivedBalesCount.value)} unreceived loaded bales where Received is 0 or null.`
                : `Listing all ${formatNumber(tfDetailLoadedBalesCount.value)} loaded bales, including ${formatNumber(tfDetailUnreceivedBalesCount.value)} with Received at 0 or null.`
        ))
        const tableRecordCountText = computed(() => {
            if (isTfDetailReport.value) {
                if (!hasGeneratedReport.value) return ''
                if (showUnreceivedBalesOnly.value) {
                    return `Showing ${formatNumber(displayedTfDetailRecords.value.length)} unreceived loaded bales out of ${formatNumber(loadedTfDetailRecords.value.length)} loaded bales`
                }
                return `Showing ${formatNumber(displayedTfDetailRecords.value.length)} loaded bales for the selected TF report`
            }
            if (totalRecords.value === null) return ''
            return `Total records: ${formatNumber(totalRecords.value)}`
        })
        const sortCompare = (a, b) => {
            const aIsSummary = a && a[TF_DETAIL_SUMMARY_KEY]
            const bIsSummary = b && b[TF_DETAIL_SUMMARY_KEY]
            if (aIsSummary && bIsSummary) return 0
            if (aIsSummary) return -1
            if (bIsSummary) return 1
            return null
        }
        const tbodyTrClass = (item, type) => {
            if (type !== 'row' || !item) return ''
            return item[TF_DETAIL_SUMMARY_KEY] ? 'table-active' : ''
        }
        const tfDetailSummary = computed(() => {
            if (!isTfDetailReport.value || !records.value.length) return null
            const transporterRaw = getFirstNonEmptyFromRecords(records.value, [
                'transporter_name',
                'transporterName',
                'transporter',
                'transporter_full_name',
                'transporterFullName',
            ])
            const transporterFirstName = transporterRaw
                ? String(transporterRaw).trim().split(/\s+/)[0]
                : 'N/A'

            const driverName = getFirstNonEmptyFromRecords(records.value, [
                'Driver_full_Name',
            ]) || 'N/A'

            const sourceName = getFirstNonEmptyFromRecords(records.value, [
                'source_name',
                'sourceName',
                'source',
                'source_location',
                'sourceLocation',
            ]) || 'N/A'

            const destinationName = getFirstNonEmptyFromRecords(records.value, [
                'destination_name',
                'destinationName',
                'destination',
                'destination_location',
                'destinationLocation',
            ]) || 'N/A'

            const plateNumber = getFirstNonEmptyFromRecords(records.value, [
                'Plate_Number',
            ]) || 'N/A'

            let minLoadedAt = null
            let maxLoadedAt = null
            records.value.forEach(item => {
                const date = parseDateTime(item.loaded_at)
                if (!date) return
                const time = date.getTime()
                if (minLoadedAt === null || time < minLoadedAt) minLoadedAt = time
                if (maxLoadedAt === null || time > maxLoadedAt) maxLoadedAt = time
            })
            const loadingTimeMinutes = (minLoadedAt !== null && maxLoadedAt !== null)
                ? (maxLoadedAt - minLoadedAt) / 60000
                : null

            const purchaseMassTotal = sumNumeric(records.value, [
                'purchase_mass',
                'purchaseMass',
                'purchase_weight',
                'purchaseWeight',
            ])
            const dispatchMassTotal = sumNumeric(records.value, [
                'Dispatched',
                'dispatched',
                'dispatch_mass',
                'dispatchMass',
            ])
            const receivedMassTotal = sumNumeric(records.value, [
                'Received',
                'received',
                'received_mass',
                'receivedMass',
            ])

            const lossDispatchVsPurchase = calcLossPercentDispatchVsPurchase(dispatchMassTotal, purchaseMassTotal)
            const lossDispatchVsReceive = calcLossPercentDispatchVsReceive(receivedMassTotal, dispatchMassTotal)

            return {
                transporterFirstName,
                driverName,
                sourceName,
                destinationName,
                plateNumber,
                loadingTimeMinutes: formatMinutes(loadingTimeMinutes),
                loadedBalesCount: formatNumber(tfDetailLoadedBalesCount.value),
                receivedBalesCount: formatNumber(tfDetailReceivedBalesCount.value),
                lossDispatchVsPurchase: formatPercent(lossDispatchVsPurchase),
                lossDispatchVsReceive: formatPercent(lossDispatchVsReceive),
            }
        })
        const isGenerateDisabled = computed(() => {
            if (loading.value || !selectedReportKey.value) return true
            if (isWarehouseStockMovementReport.value) {
                return !hasWarehouseId.value
            }
            if (isTfDetailReport.value) {
                if (isSuperUser.value && !filter.value.region_id) return true
                if (!isSuperUser.value && !locId.value) return true
                if (!String(filter.value.tf_number || '').trim()) return true
                return false
            }
            if (!selectAllRegions.value && !filter.value.region_id) return true
            if (!selectAllWarehouses.value && (!filter.value.warehouse_id || (Array.isArray(filter.value.warehouse_id) && filter.value.warehouse_id.length === 0))) return true
            if (!filter.value.start_date || !filter.value.end_date) return true
            return false
        })

        const loadRegions = async () => {
            try {
                const response = await store.dispatch('cromis-stock-production/locations')
                regionOptions.value = response.data.locations || response.data.data || response.data
            } catch (error) {
                console.error('Unable to load regions', error)
            }
        }

        const loadWarehouses = async (locationId = null) => {
            const params = { storage_only: true }
            if (locationId) {
                params.location_id = locationId
            } else if (!isSuperUser.value) {
                if (locId.value) params.location_id = locId.value.id
            } else if (!isWarehouseStockMovementReport.value && !showAllWarehouses.value && locId.value) params.location_id = locId.value.id

            console.log('Loading warehouses with params:', params)

            try {
                const response = await store.dispatch('cromis-stock-production/warehouses', params)
                console.log('Warehouses response:', response.data)
                warehouseOptions.value = response.data.warehouse_names || response.data.warehouses || response.data.data || response.data
            } catch (error) {
                console.error('Unable to load warehouses', error)
                warehouseOptions.value = []
            }
        }

        const onSelectAllWarehousesChange = checked => {
            if (checked) {
                filter.value.warehouse_id = null // null means all warehouses
            } else {
                filter.value.warehouse_id = [] // empty array for manual selection
            }
        }

        const toggleWarehouse = warehouseId => {
            if (!filter.value.warehouse_id) {
                filter.value.warehouse_id = []
            }
            const index = filter.value.warehouse_id.indexOf(warehouseId)
            if (index > -1) {
                filter.value.warehouse_id.splice(index, 1)
            } else {
                filter.value.warehouse_id.push(warehouseId)
            }
        }

        const changeRegion = async region => {
            filter.value.warehouse_id = selectAllWarehouses.value ? null : []
            if (!region && !selectAllRegions.value) {
                warehouseOptions.value = []
                return
            }
            await loadWarehouses(region)
        }

        watch(selectedReportKey, async newKey => {
            records.value = []
            totalRecords.value = 0
            errorMessage.value = null
            hasGeneratedReport.value = false
            showUnreceivedBalesOnly.value = false
            filter.value.start_date = null
            filter.value.end_date = null
            filter.value.tf_number = ''
            if (!newKey) {
                filter.value.region_id = null
                filter.value.warehouse_id = null
                selectAllRegions.value = false
                selectAllWarehouses.value = false
                return
            }

            if (newKey === 'warehouse_stock_movements') {
                filter.value.region_id = null
                filter.value.warehouse_id = null
                selectAllRegions.value = false
                selectAllWarehouses.value = false
                await loadWarehouses()
            }
        })

        const generateReport = async () => {
            errorMessage.value = null

            if (isWarehouseStockMovementReport.value) {
                if (!hasWarehouseId.value) {
                    errorMessage.value = 'Please select a warehouse.'
                    return
                }

                await fetchReport({
                    reportType: selectedReportKey.value,
                    warehouse_id: String(filter.value.warehouse_id),
                })
                hasGeneratedReport.value = true
                return
            }

            if (isTfDetailReport.value) {
                let regionId = null
                if (isSuperUser.value) {
                    regionId = filter.value.region_id
                } else if (locId.value) {
                    regionId = locId.value.id ?? locId.value
                }

                if (!regionId) {
                    errorMessage.value = 'Please select a location.'
                    return
                }

                const tfNumber = String(filter.value.tf_number || '').trim()
                if (!tfNumber) {
                    errorMessage.value = 'Please enter a TF number.'
                    return
                }

                const params = {
                    reportType: selectedReportKey.value,
                    region_id: regionId,
                    tf_number: tfNumber,
                }

                await fetchReport(params)
                hasGeneratedReport.value = true
                return
            }

            if (!selectAllRegions.value && !filter.value.region_id) {
                errorMessage.value = 'Please select a region or check "Select All Regions".'
                return
            }

            if (!selectAllWarehouses.value && (!filter.value.warehouse_id || (Array.isArray(filter.value.warehouse_id) && filter.value.warehouse_id.length === 0))) {
                errorMessage.value = 'Please select warehouses or check "Select All Warehouses".'
                return
            }

            if (!filter.value.start_date || !filter.value.end_date) {
                errorMessage.value = 'Please select both start and end dates.'
                return
            }

            // Prepare parameters
            const params = {
                reportType: selectedReportKey.value,
                start_date: formatDate(filter.value.start_date),
                end_date: formatDate(filter.value.end_date),
            }

            // Handle location_id (from region)
            if (selectAllRegions.value) {
                params.location_id = null
            } else {
                params.location_id = filter.value.region_id
            }

            // Handle warehouse_id
            if (selectAllWarehouses.value) {
                params.warehouse_id = null
            } else if (Array.isArray(filter.value.warehouse_id)) {
                params.warehouse_id = filter.value.warehouse_id.join(',')
            } else {
                params.warehouse_id = filter.value.warehouse_id !== null && filter.value.warehouse_id !== undefined
                    ? String(filter.value.warehouse_id)
                    : null
            }

            await fetchReport(params)
            hasGeneratedReport.value = true
        }

        const getReportFileNameParts = () => {
            const fileNameParts = [currentReportConfig.value.label.replace(/[^a-zA-Z0-9]/g, '_')]
            if (isTfDetailReport.value) {
                const safeTfNumber = String(filter.value.tf_number || '').trim() || 'tf_number'
                fileNameParts.push(safeTfNumber)
                fileNameParts.push(showUnreceivedBalesOnly.value ? 'unreceived_bales_only' : 'all_loaded_bales')
            } else if (isWarehouseStockMovementReport.value) {
                fileNameParts.push(`warehouse_${filter.value.warehouse_id}`)
            } else {
                fileNameParts.push(formatDate(filter.value.start_date), formatDate(filter.value.end_date))
            }
            return fileNameParts.filter(Boolean)
        }

        const getFormattedCellValue = (item, column) => {
            const rawValue = item[column.key]
            if (column.formatter) return column.formatter(rawValue)
            return rawValue === null || rawValue === undefined ? '' : rawValue
        }

        const getReportHeaderDetails = () => {
            const title = currentReportConfig.value.label
            const producer = userData.fullName || userData.username || 'Unknown User'
            const executionDateTime = new Date().toLocaleString()

            let reportParameters = []
            let reportParametersRight = []
            if (isTfDetailReport.value) {
                let locationLabel = 'N/A'
                if (isSuperUser.value) {
                    const selectedRegion = regionOptions.value.find(r => r.id === filter.value.region_id)
                    locationLabel = selectedRegion ? selectedRegion.name : 'N/A'
                } else if (locId.value) {
                    locationLabel = locId.value.name || locId.value.location_name || locId.value.title || 'Assigned Location'
                }

                reportParameters = [
                    { label: 'Location', value: locationLabel },
                    { label: 'TF Number', value: filter.value.tf_number || 'N/A' },
                    { label: 'View', value: showUnreceivedBalesOnly.value ? 'Unreceived loaded bales only' : 'All loaded bales' },
                ]

                reportParametersRight = [
                    { label: 'Bales Loaded (> 0)', value: formatNumber(tfDetailLoadedBalesCount.value) },
                    { label: 'Bales Received (> 0)', value: formatNumber(tfDetailReceivedBalesCount.value) },
                ]

                if (tfDetailSummary.value) {
                    reportParameters.push(
                        { label: 'Transporter (First Name)', value: tfDetailSummary.value.transporterFirstName },
                        { label: 'Driver', value: tfDetailSummary.value.driverName },
                        { label: 'Plate Number', value: tfDetailSummary.value.plateNumber },
                    )

                    reportParametersRight = [
                        ...reportParametersRight,
                        { label: 'Source', value: tfDetailSummary.value.sourceName },
                        { label: 'Destination', value: tfDetailSummary.value.destinationName },
                        { label: 'Loss % (Dispatch vs Purchase)', value: tfDetailSummary.value.lossDispatchVsPurchase },
                        { label: 'Loss % (Dispatch vs Receive)', value: tfDetailSummary.value.lossDispatchVsReceive },
                        { label: 'Loading Time', value: tfDetailSummary.value.loadingTimeMinutes },
                    ]
                }
            } else if (isWarehouseStockMovementReport.value) {
                const selectedWarehouse = warehouseOptions.value.find(w => String(w.id) === String(filter.value.warehouse_id))
                const warehouseLabel = selectedWarehouse ? selectedWarehouse.name : 'N/A'

                reportParameters = [
                    { label: 'Warehouse', value: warehouseLabel },
                ]
            } else {
                const subtitle = `From ${formatDate(filter.value.start_date)} to ${formatDate(filter.value.end_date)}`
                let warehouseLabel = 'All Warehouses'
                if (!selectAllWarehouses.value) {
                    if (Array.isArray(filter.value.warehouse_id)) {
                        const selectedWarehouses = warehouseOptions.value.filter(w => filter.value.warehouse_id.includes(w.id))
                        warehouseLabel = selectedWarehouses.map(w => w.name).join(', ')
                    } else {
                        const selectedWarehouse = warehouseOptions.value.find(w => w.id === filter.value.warehouse_id)
                        warehouseLabel = selectedWarehouse ? selectedWarehouse.name : 'N/A'
                    }
                }

                reportParameters = [
                    { label: 'Warehouse', value: warehouseLabel },
                    { label: 'Period', value: subtitle },
                ]
            }

            return {
                title,
                producer,
                executionDateTime,
                reportParameters,
                reportParametersRight,
            }
        }

        const exportPdf = () => {
            const doc = new JsPDF('landscape')
            const {
                title,
                producer,
                executionDateTime,
                reportParameters,
                reportParametersRight,
            } = getReportHeaderDetails()

            const headerStartY = addReportHeader(doc, {
                logo,
                companyName,
                companyAddress,
                reportTitle: title,
                reportParameters,
                reportParametersRight,
            })

            const head = currentColumns.value.map(column => column.label)
            const body = tableItems.value.map(item => currentColumns.value.map(column => getFormattedCellValue(item, column)))

            doc.autoTable({
                head: [head],
                body,
                startY: headerStartY,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                margin: { left: 14, right: 14 },
                didDrawPage: createReportFooterCallback(doc, {
                    generatedBy: producer,
                    executionDateTime,
                }),
            })

            const fileName = `${getReportFileNameParts().join('_')}.pdf`
            doc.save(fileName)
        }

        const exportExcel = () => {
            const {
                title,
                producer,
                executionDateTime,
                reportParameters,
                reportParametersRight,
            } = getReportHeaderDetails()
            exportTableToExcel({
                title,
                fileName: getReportFileNameParts().join('_'),
                companyName,
                companyAddress,
                reportParameters,
                reportParametersRight,
                generatedBy: producer,
                executionDateTime,
                columns: currentColumns.value,
                items: tableItems.value,
                getCellValue: getFormattedCellValue,
            })
        }

        watch(showAllWarehouses, async newVal => {
            filter.value.region_id = null
            filter.value.warehouse_id = null
            // Regions are now always loaded, so no need to conditionally load them here
            if (locId.value && !newVal) {
                filter.value.region_id = locId.value.id
            }
            await loadWarehouses()
        })

        onMounted(async () => {
            console.log('onMounted running, isSuperUser:', isSuperUser.value, 'role:', role.value, 'locId:', locId.value)
            // Always load regions since region selection is now always shown
            await loadRegions()

            if (isSuperUser.value) {
                await loadWarehouses()
                return
            }

            if (locId.value) {
                filter.value.region_id = locId.value.id
                await loadWarehouses()
            }
        })

        return {
            selectedReportKey,
            reportOptions,
            currentReportConfig,
            currentColumns,
            isTfDetailReport,
            isWarehouseStockMovementReport,
            isGenerateDisabled,
            selectAllRegions,
            selectAllWarehouses,
            showUnreceivedBalesOnly,
            records,
            loading,
            totalRecords,
            filter,
            role,
            regions,
            warehouses,
            regionOptions,
            warehouseOptions,
            errorMessage,
            generateReport,
            changeRegion,
            onSelectAllWarehousesChange,
            toggleWarehouse,
            exportPdf,
            exportExcel,
            isSuperUser,
            showAllWarehouses,
            tableItems,
            currentEmptyText,
            tfDetailToggleHelpText,
            tableRecordCountText,
            sortCompare,
            tbodyTrClass,
            tfDetailSummary,
        }
    },
}
</script>

<style scoped>
.warehouse-selection {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
.warehouse-option {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.35rem;
}
.warehouse-checkbox {
  margin-right: 0.5rem;
}
</style>
